import { useCallback, useRef, useState, useEffect } from "react";

// Free web barcode scanning. Same contract as 'useScanSix', the
// licensed Scandit hook this replaced in 2026-07 (it lived in
// client/src/scandit/ — see git history). Engine choice per platform:
// - Android Chrome ships a native 'BarcodeDetector' (backed by the
//   same Google/ML Kit models Play Services uses) — free and fast.
// - iOS Safari/WebKit has no BarcodeDetector, so we fall back to the
//   'barcode-detector' ponyfill (ZXing-C++ compiled to WASM).
// Ponyfill docs: https://github.com/Sec-ant/barcode-detector

// Same symbologies the Scandit setup enabled: EAN-13/UPC-A + Code 128.
// (BarcodeDetector treats upc_a as its own format; ZXing folds it
// into ean_13 with a leading zero — normalizeScanKitCode handles it.)
const FORMATS = ["ean_13", "upc_a", "code_128"];

// MANUAL FIX carried over from the Scandit hook: a 13-digit EAN starting
// with '0' is a 12-digit UPC-A in disguise; trim when the Sku's
// Circle prefers UPC-A. Exported for unit tests.
export function normalizeScanKitCode(format, raw, wants12digitEAN) {
  if (format === "ean_13" && wants12digitEAN && raw.length === 13 &&
      raw.startsWith("0")) {
    return raw.substring(1);
  }
  return raw;
}

// Prefer the built-in detector only when it recognizes every format
// we need; otherwise load the WASM ponyfill.
async function makeDetector() {
  if (typeof window.BarcodeDetector === "function") {
    try {
      const supported = await window.BarcodeDetector
                                                  .getSupportedFormats();
      if (FORMATS.every((f) => supported.includes(f))) {
        return new window.BarcodeDetector({ formats: FORMATS });
      }
    } catch (e) {
      // Fall through to the ponyfill on any native-detector oddity.
      console.warn("Native BarcodeDetector unusable:", e);
    }
  }

  // Dynamic import: keeps the WASM glue out of the main bundle and
  // out of jest (jsdom has no camera to exercise it anyway).
  const { BarcodeDetector, prepareZXingModule } =
    await import("barcode-detector/ponyfill");

  // Serve the .wasm ourselves (copied by 'copy:zxing' in
  // package.json) instead of the package's default jsDelivr CDN:
  // no third-party dependency at runtime, and the PWA service
  // worker can cache it as a same-origin static asset.
  prepareZXingModule({
    overrides: {
      locateFile: (path, prefix) =>
        path.endsWith(".wasm")
          ? window.location.origin + "/wasm/" + path
          : prefix + path,
    },
  });
  return new BarcodeDetector({ formats: FORMATS });
}

export function useScanKit({ wants12digitEAN = true, onScan }) {
  const isInit = useRef(false);
  const [ready, setReady] = useState(false);
  const isAlive = useRef(true); // useEffect cleanup flips this false

  // Refs to live resources so cleanup can always find them.
  const resources = useRef({
    stream: null,
    video: null,
    container: null,
  });

  const stopStream = useCallback(() => {
    const { stream, video } = resources.current;
    if (stream) stream.getTracks().forEach((t) => t.stop());
    if (video) video.srcObject = null;
    resources.current.stream = null;
  }, []);

  // Initialization lives in a callback ref so it runs exactly when
  // the container <div> mounts.
  const containerRef = useCallback(async (node) => {
    if (node === null || isInit.current) return;
    isInit.current = true;
    isAlive.current = true;
    resources.current.container = node;

    try {
      const detector = await makeDetector();
      if (!isAlive.current) return;

      const stream = await navigator.mediaDevices.getUserMedia({
        // Rear camera; FullHD helps small 1D codes, browsers clamp
        // to the nearest supported size.
        video: {
          facingMode: "environment",
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
        audio: false,
      });
      // CHECK: unmounted while the permission prompt was open?
      if (!isAlive.current) {
        stream.getTracks().forEach((t) => t.stop());
        return;
      }
      resources.current.stream = stream;

      const video = document.createElement("video");
      // iOS Safari refuses inline autoplay without these.
      video.setAttribute("playsinline", "");
      video.muted = true;
      video.autoplay = true;
      video.srcObject = stream;
      video.style.width = "100%";
      video.style.height = "100%";
      video.style.objectFit = "cover";
      resources.current.video = video;
      node.appendChild(video);

      // Scanning-guide box (Scandit's overlay drew this for us).
      const guide = document.createElement("div");
      guide.style.cssText =
        "position:absolute;top:50%;left:50%;" +
        "transform:translate(-50%,-50%);" +
        "width:60%;height:38%;border:2px solid #fff;" +
        "border-radius:8px;box-shadow:0 0 0 9999px rgba(0,0,0,.35);" +
        "pointer-events:none;";
      node.appendChild(guide);

      await video.play();
      if (!isAlive.current) return;
      setReady(true);

      // Detect loop. requestVideoFrameCallback runs once per decoded
      // camera frame (Safari 15.4+, Chrome 83+); rAF is the fallback.
      const scheduleNext = (fn) => {
        if (video.requestVideoFrameCallback) {
          video.requestVideoFrameCallback(fn);
        } else {
          requestAnimationFrame(fn);
        }
      };

      const tick = async () => {
        if (!isAlive.current || !resources.current.stream) return;
        try {
          if (video.readyState >= 2) { // HAVE_CURRENT_DATA
            const barcodes = await detector.detect(video);
            const hit = barcodes.find((b) => b.rawValue);
            if (hit && isAlive.current) {
              // Stop the camera before handing off: saves battery,
              // prevents double scans.
              stopStream();
              const code = normalizeScanKitCode(
                hit.format, hit.rawValue, wants12digitEAN);
              onScan?.(code);
              return;
            }
          }
        } catch (e) {
          // A single bad frame shouldn't kill the loop.
          console.warn("scan frame error:", e);
        }
        scheduleNext(tick);
      };
      scheduleNext(tick);
    } catch (err) {
      console.error("ScanKit init error:", err);
      stopStream();
      isInit.current = false; // allow a retry on next mount
    }
  }, [wants12digitEAN, onScan, stopStream]);
  // onScan must be memoized in the parent (useCallback), or a
  // re-render mid-scan leaves the loop holding a stale callback.

  useEffect(() => {
    isAlive.current = true;
    const currentResources = resources.current;

    return () => {
      isAlive.current = false; // kills the detect loop + async init
      const { video, container } = currentResources;
      stopStream();
      if (video && container && container.contains(video)) {
        container.innerHTML = "";
      }
      resources.current = {
        stream: null, video: null, container: null,
      };
      isInit.current = false;
      setReady(false);
    };
  }, [stopStream]);

  return { containerRef, ready };
}
